/**
 * Bank reconciliation page (admin only).
 *
 * Sections:
 *  - Top: import block (Qonto CSV/XLSX) + month filter
 *  - Suggestions: for each unreconciled bank transaction, up to 3 candidate
 *    internal records sorted by confidence. One-click Accept makes the match.
 *  - All transactions: simple table with status + actions.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bank, Upload, CheckCircle, ArrowsCounterClockwise, Trash,
  CurrencyEur, X, Sparkle, ArrowSquareOut,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  company_expense: 'Dépense entreprise',
  employee_expense: 'Achat société (salarié)',
  subcontractor_invoice: 'Facture fournisseur',
};

const fmtEur = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const BankReconciliationPage = () => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [suggestions, setSuggestions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState('suggestions');

  const fetchData = useCallback(async () => {
    setBusy(true);
    try {
      const [s, t] = await Promise.all([
        axios.get(`${API_URL}/api/finance/bank/suggestions?month=${month}&min_score=50`, { withCredentials: true }),
        axios.get(`${API_URL}/api/finance/bank/transactions?month=${month}&status=all`, { withCredentials: true }),
      ]);
      setSuggestions(s.data || []);
      setTransactions(t.data || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setBusy(false);
    }
  }, [month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleImport = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API_URL}/api/finance/bank/import`, fd, { withCredentials: true });
      toast.success(`${r.data.imported} transaction(s) importée(s)`);
      // If no matchable suggestion will appear, jump to the full list so the
      // user immediately sees the imported rows instead of an empty list.
      if (r.data.imported > 0) setTab('all');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de l\'import');
    } finally {
      setUploading(false);
    }
  };

  const handleAccept = async (txId, target) => {
    try {
      await axios.post(`${API_URL}/api/finance/bank/match`, {
        transaction_id: txId,
        target_type: target.type,
        target_id: target.id,
      }, { withCredentials: true });
      toast.success('Rapprochement validé');
      fetchData();
    } catch (e) {
      toast.error('Échec du rapprochement');
    }
  };

  const handleUnmatch = async (txId) => {
    if (!window.confirm('Annuler ce rapprochement ?')) return;
    try {
      await axios.post(`${API_URL}/api/finance/bank/unmatch`, { transaction_id: txId }, { withCredentials: true });
      fetchData();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (txId) => {
    if (!window.confirm('Supprimer cette transaction bancaire ? (utile pour les imports en double)')) return;
    try {
      await axios.delete(`${API_URL}/api/finance/bank/transactions/${txId}`, { withCredentials: true });
      fetchData();
    } catch {
      toast.error('Erreur');
    }
  };

  const txWithSuggestions = suggestions.filter((s) => s.matches?.length > 0);
  const txWithoutSuggestions = suggestions.filter((s) => !s.matches?.length);

  return (
    <div data-testid="bank-reco-page" style={{ padding: '8px 0' }}>
      {/* --- Header --- */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Bank size={28} weight="duotone" style={{ color: '#1D4ED8' }} />
          <div>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Rapprochement bancaire</h2>
            <p style={{ margin: 0, fontSize: '0.78rem', color: '#5E5F66' }}>
              Importer le relevé Qonto et matcher les opérations aux dépenses, notes de frais et factures.
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="bank-month"
            className="form-input"
            style={{ height: 38 }}
          />
          <label className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            {uploading ? <Sparkle size={16} weight="fill" /> : <Upload size={16} />}
            {uploading ? 'Import...' : 'Importer relevé Qonto'}
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              data-testid="bank-import-input"
              style={{ display: 'none' }}
              onChange={(e) => { if (e.target.files?.[0]) { handleImport(e.target.files[0]); e.target.value = ''; } }}
              disabled={uploading}
            />
          </label>
        </div>
      </div>

      {/* --- Tab switch --- */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #E2E2E6', marginBottom: 16 }}>
        {[
          { key: 'suggestions', label: `Suggestions (${txWithSuggestions.length})` },
          { key: 'all', label: `Toutes les opérations (${transactions.length})` },
        ].map((it) => (
          <button
            key={it.key}
            onClick={() => setTab(it.key)}
            data-testid={`bank-tab-${it.key}`}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: '8px 14px', fontWeight: 600, fontSize: '0.86rem',
              borderBottom: tab === it.key ? '2px solid #1D4ED8' : '2px solid transparent',
              color: tab === it.key ? '#1D4ED8' : '#5E5F66',
            }}
          >
            {it.label}
          </button>
        ))}
      </div>

      {busy && <p style={{ color: '#5E5F66', fontSize: '0.85rem' }}>Chargement...</p>}

      {/* --- Suggestions tab --- */}
      {tab === 'suggestions' && (
        <>
          {txWithSuggestions.length === 0 && !busy && (
            <div style={{ padding: 24, textAlign: 'center', color: '#5E5F66', background: '#F4F5F7', borderRadius: 10 }}>
              <CurrencyEur size={36} weight="duotone" />
              <p style={{ marginTop: 8 }}>Aucune suggestion pour le moment. Importe un relevé ou attends d'avoir des dépenses à rapprocher.</p>
            </div>
          )}
          {txWithSuggestions.map(({ transaction, matches }) => (
            <div key={transaction.id} data-testid={`bank-suggestion-${transaction.id}`} style={{ border: '1px solid #E2E2E6', borderRadius: 10, padding: 12, marginBottom: 12, background: 'white' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#5E5F66', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {transaction.date}
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: 700, color: '#0F172A' }}>
                    {transaction.counterparty || '—'}
                  </div>
                  {transaction.reference && (
                    <div style={{ fontSize: '0.78rem', color: '#5E5F66' }}>{transaction.reference}</div>
                  )}
                </div>
                <div style={{
                  fontSize: '1.05rem', fontWeight: 800,
                  color: transaction.amount_ttc < 0 ? '#C62828' : '#16A34A',
                }}>
                  {transaction.amount_ttc < 0 ? '−' : '+'}{fmtEur(Math.abs(transaction.amount_ttc))}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                {matches.map((m) => (
                  <div key={`${m.type}-${m.id}`} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                    background: '#F8FAFF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '8px 12px',
                  }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: '0.72rem', color: '#1D4ED8', fontWeight: 700, textTransform: 'uppercase' }}>
                        {TYPE_LABELS[m.type]} · {m.date} · score {m.score}
                      </div>
                      <div style={{ fontSize: '0.86rem', color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {m.supplier || '—'} {m.description ? ` · ${m.description}` : ''}
                      </div>
                    </div>
                    <div style={{ fontWeight: 700, color: '#0F172A', minWidth: 80, textAlign: 'right' }}>
                      {fmtEur(m.amount_ttc)}
                    </div>
                    <button
                      type="button"
                      data-testid={`bank-accept-${transaction.id}-${m.id}`}
                      onClick={() => handleAccept(transaction.id, m)}
                      className="btn btn-primary btn-sm"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      <CheckCircle size={14} /> Rapprocher
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {txWithoutSuggestions.length > 0 && (
            <details style={{ marginTop: 16 }}>
              <summary style={{ cursor: 'pointer', color: '#5E5F66', fontSize: '0.84rem' }}>
                {txWithoutSuggestions.length} opération(s) sans suggestion automatique
              </summary>
              <div style={{ padding: '10px 0' }}>
                {txWithoutSuggestions.map(({ transaction }) => (
                  <div key={transaction.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', borderBottom: '1px solid #EEE', fontSize: '0.84rem' }}>
                    <span>{transaction.date} · {transaction.counterparty || '—'}</span>
                    <span style={{ fontWeight: 700, color: transaction.amount_ttc < 0 ? '#C62828' : '#16A34A' }}>
                      {fmtEur(Math.abs(transaction.amount_ttc))}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}

      {/* --- All transactions tab --- */}
      {tab === 'all' && (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Contrepartie</th>
                <th>Référence</th>
                <th style={{ textAlign: 'right' }}>Montant</th>
                <th>Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr key={tx.id} data-testid={`bank-tx-${tx.id}`}>
                  <td>{tx.date}</td>
                  <td>{tx.counterparty || '—'}</td>
                  <td style={{ color: '#5E5F66', fontSize: '0.82rem' }}>
                    {tx.qonto_url ? (
                      <a
                        href={tx.qonto_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`qonto-link-${tx.id}`}
                        title="Ouvrir cette opération sur Qonto"
                        style={{ color: '#1D4ED8', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        {tx.reference || 'Voir sur Qonto'}
                        <ArrowSquareOut size={12} />
                      </a>
                    ) : (
                      tx.reference || ''
                    )}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: tx.amount_ttc < 0 ? '#C62828' : '#16A34A' }}>
                    {fmtEur(tx.amount_ttc)}
                  </td>
                  <td>
                    {tx.reconciled_with ? (
                      <span style={{ background: '#DCFCE7', color: '#166534', padding: '2px 8px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700 }}>
                        Rapproché
                      </span>
                    ) : (
                      <span style={{ background: '#FEF3C7', color: '#92400E', padding: '2px 8px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700 }}>
                        Non rapproché
                      </span>
                    )}
                  </td>
                  <td>
                    {tx.reconciled_with ? (
                      <button className="btn btn-secondary btn-sm btn-icon" onClick={() => handleUnmatch(tx.id)} title="Annuler le rapprochement">
                        <ArrowsCounterClockwise size={14} />
                      </button>
                    ) : (
                      <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(tx.id)} title="Supprimer">
                        <Trash size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {transactions.length === 0 && (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: 24, color: '#5E5F66' }}>Aucune transaction pour ce mois.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BankReconciliationPage;
